/**
 * Steps the product-grid-showcase arrows one product at a time, instead of the
 * native default of advancing by however many cards are visible at once
 * (slideshow.js's nextIndex/previousIndex getters use `current ± visibleSlides.length`
 * on a multi-up layout — built for "page through a whole new set," not "reveal just
 * the next product"). Client request: always single-step here, on every screen size.
 *
 * Removing the native on:click delegation is the only way to stop it from matching
 * these buttons — it's registered on `document` in the capture phase, which always
 * runs before a listener added directly on the button, however that listener is
 * attached.
 *
 * The native component also disables "next"/"previous" using that same
 * visible-count-based math (`next.disabled` when `current + visibleSlides.length >=
 * total`), completely independent of how the click is actually stepping — with
 * single steps, that disables "next" the moment there are fewer remaining products
 * than cards visible at once, even though more products are still genuinely
 * unseen. This section is rendered with infinite: false (real disabling wanted at
 * the true ends, not a loop), so the disabled state is set manually here instead,
 * using the single-step-correct condition, right after every select() call —
 * overriding whatever the native logic computed for the same change.
 */
function initSingleStepArrows(slideshow) {
  const previousButton = slideshow.querySelector('slideshow-arrows .slideshow-control--previous');
  const nextButton = slideshow.querySelector('slideshow-arrows .slideshow-control--next');

  function updateDisabledState() {
    const slides = slideshow.slides;
    if (!slides || !slides.length) return;

    const lastIndex = slides.length - 1;
    if (previousButton) previousButton.disabled = slideshow.current <= 0;
    if (nextButton) nextButton.disabled = slideshow.current >= lastIndex;
  }

  function step(direction) {
    const slides = slideshow.slides;
    if (!slides || !slides.length) return;

    const lastIndex = slides.length - 1;
    const targetIndex = Math.min(Math.max(slideshow.current + direction, 0), lastIndex);
    slideshow.select(targetIndex);
    updateDisabledState();
  }

  if (previousButton) {
    previousButton.removeAttribute('on:click');
    previousButton.addEventListener('click', () => step(-1));
  }

  if (nextButton) {
    nextButton.removeAttribute('on:click');
    nextButton.addEventListener('click', () => step(1));
  }

  updateDisabledState();
}

function initAllProductShowcaseSlideshows() {
  document.querySelectorAll('.product-showcase__slideshow').forEach(initSingleStepArrows);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllProductShowcaseSlideshows);
} else {
  initAllProductShowcaseSlideshows();
}
